/**
 * Account UI: sign in / create account / sign out, plus the sync status line.
 *
 * All of this is optional from the user's point of view. Signed out, the app
 * behaves exactly as it did before an account existed: local-only, no
 * network traffic at all.
 */
import { ClientResponseError } from 'pocketbase';
import pb from './lib/pocketbase';
import * as Sync from './sync';
import { PB_CONFIGURED } from './config';
import { input, must, onClick } from './dom';
import { hasLocalWorkouts } from './lib/store';
import type { SyncStatusKind } from './types';

type AuthMode = 'signin' | 'signup';

let authMode: AuthMode = 'signin';
let onDataReset: (() => void) | null = null;

/**
 * The last sync outcome. The empty state reads it so that, signed in, it can
 * say "loading" or "offline" while the answer is still on its way, instead of
 * "nothing here" for the second before the history arrives.
 */
let lastStatus: SyncStatusKind = 'idle';

export function setStatus(text: string, kind: SyncStatusKind = 'idle'): void {
  const node = must('sync-status');
  node.textContent = text;
  node.className = `sync-status sync-${kind}`;
  lastStatus = kind;
  renderEmptyState();
}

/**
 * Where your workouts are kept, plus the controls for it.
 *
 * Signed out it names the current mode and offers the alternative; signed in it
 * confirms backup is handled and switches to the account actions. This replaced
 * a separate account bar that sat above it and said "on this device" in its own
 * words -- the same fact stated twice, in two components, is neither clear nor
 * concise.
 *
 * Restore stays in the card at the bottom of the page rather than here: it is a
 * rare, one-off action, and a third button crowds the panel that sits directly
 * above Log Workout.
 */
function renderStoragePanel(): void {
  const text = must('storage-panel-text');
  const localActions = must('storage-panel-actions');
  const accountActions = must('storage-panel-account');
  const signedIn = PB_CONFIGURED && pb.authStore.isValid;

  if (signedIn) {
    // Just the address. "Synced" beside it already says the state, and
    // "Sync now / Sign out" already imply an account -- spelling out "backed
    // up in the cloud" repeats both and wraps the line onto two rows.
    text.textContent = String(pb.authStore.record?.['email'] ?? 'Signed in');
    localActions.hidden = true;
    accountActions.hidden = false;
    return;
  }

  accountActions.hidden = true;
  localActions.hidden = false;

  // Lead with what an account is actually for. Syncing is the more valuable
  // option for most people, so it is recommended rather than merely offered --
  // while manual backup stays reachable, quietly, for anyone who will not
  // create one. Working with no account at all is still true and still why
  // there is no barrier to a first visit; it is just not the pitch.
  //
  // "Create" rather than "log in": a first-time visitor has no credentials,
  // and being told to log in to something they have never had is confusing.
  // No icon: nothing pictures "no account needed", so anything here would be
  // decoration. The signed-in line keeps ☁️ because it restates "in the cloud".
  text.textContent = 'No account needed to try';

  // Without a configured backend there is no cloud to offer, and no account
  // to sign in to -- so say the honest thing instead.
  const canUseCloud = PB_CONFIGURED;
  must('panel-signup-btn').hidden = !canUseCloud;
  must('account-signin-link').hidden = !canUseCloud;
  if (!canUseCloud) {
    text.textContent = 'Stored on this device — back up so a cleared browser cannot lose them';
  }
}

type EmptyStateAction = 'signin' | 'restore' | 'sync';

/**
 * What the Progress card says when this device holds no workouts.
 *
 * An empty device is one of two people: a first-time visitor, or a returning
 * one on a new phone or after a cleared cache -- the very case the account
 * exists for. Signed out, nothing tells them apart, so the message speaks to
 * both and makes the returning visitor's two routes tappable. Signed in, the
 * last sync result says which it is.
 *
 * The view only shows or hides this box (stats.ts); the words are decided here
 * because every change that alters them -- sign in, sign out, a sync result --
 * already arrives in this module.
 *
 * Built with createElement: the tappable words are buttons, and an inline
 * handler would silently die under the production CSP.
 */
function renderEmptyState(): void {
  const box = must('stats-empty');
  box.replaceChildren();

  const paragraph = (...parts: (string | HTMLElement)[]): void => {
    const p = document.createElement('p');
    p.append(...parts);
    box.appendChild(p);
  };
  const link = (label: string, action: EmptyStateAction): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'link-button';
    button.dataset['action'] = action;
    button.textContent = label;
    return button;
  };

  // No cloud configured means no account to sign in to; only restore applies.
  if (!PB_CONFIGURED) {
    paragraph(
      'No workouts on this device yet. Log one above, or ',
      link('restore your workouts from a backup', 'restore'),
      '.',
    );
    return;
  }

  if (pb.authStore.isValid) {
    if (lastStatus === 'busy') {
      paragraph('Loading your workouts…');
    } else if (lastStatus === 'offline') {
      paragraph("You're offline — your workouts will appear once you're back online.");
    } else if (lastStatus === 'error') {
      paragraph("Couldn't reach your account. ", link('Sync now', 'sync'), ' to try again.');
    } else {
      paragraph('No workouts in your account yet. Log your first one above!');
    }
    return;
  }

  // One paragraph per visitor: each can skip the one that is not theirs.
  paragraph('No workouts on this device yet.');
  paragraph('New here? Log your first workout above.');
  paragraph(
    'Been here before? ',
    link('Sign in', 'signin'),
    ' to bring back your workouts, or ',
    link('restore your workouts from a backup', 'restore'),
    '.',
  );
}

export function render(): void {
  renderStoragePanel();
  if (!PB_CONFIGURED || !pb.authStore.isValid) setStatus('', 'idle');
  renderEmptyState();
}

function updateAuthModalMode(): void {
  const isSignup = authMode === 'signup';
  must('auth-title').textContent = isSignup ? 'Create Account' : 'Sign In';
  must('auth-submit').textContent = isSignup ? 'Create Account' : 'Sign In';
  must('auth-toggle-text').textContent = isSignup
    ? 'Already have an account?'
    : 'No account yet?';
  must('auth-toggle-link').textContent = isSignup ? 'Sign in' : 'Create one';
  must('auth-forgot').hidden = isSignup;

  // Shown in both modes, because the question "what happens to the workouts
  // already on this device?" matters just as much when signing in on a second
  // device as when creating an account. Sync merges the two sets; say so.
  const note = must('auth-merge-note');
  note.hidden = !hasLocalWorkouts();
  note.textContent = isSignup
    ? 'The workouts already on this device will be merged into your new account.'
    : 'The workouts already on this device will be added to your account, not replaced.';

  // What the account is for, stated before we ask for an email rather than
  // after: give the reason, then request the details.
  must('auth-blurb').textContent = isSignup
    ? 'An account keeps your workout history synced across your devices, and backs ' +
      'it up automatically. We store your email and your workouts. Nothing else.'
    : 'Signing in backs up your workouts automatically and keeps them synced across ' +
      'your devices. We store your email and your workouts. Nothing else.';
}

export function showAuthModal(mode: AuthMode = 'signin'): void {
  authMode = mode;
  must('auth-error').textContent = '';
  input('auth-email').value = '';
  input('auth-password').value = '';
  updateAuthModalMode();
  must('auth-modal').style.display = 'block';
}

export function hideAuthModal(): void {
  must('auth-modal').style.display = 'none';
}

function toggleMode(): void {
  authMode = authMode === 'signup' ? 'signin' : 'signup';
  must('auth-error').textContent = '';
  updateAuthModalMode();
}

/** PocketBase's raw validation text is not fit to show a user as-is. */
function friendlyAuthError(err: unknown): string {
  const message = err instanceof Error ? err.message : 'Something went wrong.';
  const status = err instanceof ClientResponseError ? err.status : 0;

  if (status === 400 && authMode === 'signin') return 'Incorrect email or password.';
  if (message.startsWith('email:')) {
    return 'That email is already registered, or is not valid.';
  }
  if (message.startsWith('password:')) {
    return 'That password is not accepted. Try a longer one.';
  }
  if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return message;
}

async function submitAuth(): Promise<void> {
  const email = input('auth-email').value.trim();
  const password = input('auth-password').value;
  const errorNode = must('auth-error');
  const button = must<HTMLButtonElement>('auth-submit');

  errorNode.textContent = '';

  if (!email.includes('@')) {
    errorNode.textContent = 'Please enter a valid email address.';
    return;
  }
  if (password.length < 8) {
    errorNode.textContent = 'Password must be at least 8 characters.';
    return;
  }

  button.disabled = true;
  button.textContent = authMode === 'signup' ? 'Creating…' : 'Signing in…';

  try {
    if (authMode === 'signup') {
      await pb.collection('users').create({
        email,
        password,
        passwordConfirm: password,
      });
    }
    const { record } = await pb
      .collection('users')
      .authWithPassword(email, password);
    hideAuthModal();
    render();
    await Sync.onSignedIn(record);
  } catch (err) {
    errorNode.textContent = friendlyAuthError(err);
  } finally {
    button.disabled = false;
    updateAuthModalMode();
  }
}

async function forgotPassword(): Promise<void> {
  const email = input('auth-email').value.trim();
  if (!email.includes('@')) {
    must('auth-error').textContent = 'Enter your email address first, then tap this again.';
    return;
  }

  // The same message either way: confirming which addresses have accounts
  // would leak who uses the app.
  const reassurance = 'If that address has an account, a password reset email is on its way.';
  try {
    await pb.collection('users').requestPasswordReset(email);
  } catch {
    // Intentionally swallowed, for the reason above.
  }
  must('auth-error').textContent = '';
  alert(reassurance);
}

function signOut(): void {
  const alsoClear = confirm(
    'Sign out?\n\n' +
      "OK — sign out and also remove this device's local copy (use this on a shared device).\n" +
      'Cancel — sign out but keep the workouts on this device.',
  );

  pb.authStore.clear();
  Sync.onSignedOut();

  if (alsoClear) {
    Sync.resetLocalData();
    onDataReset?.();
  }

  render();
}

export function init(options: { onDataReset: () => void }): void {
  onDataReset = options.onDataReset;
  Sync.onStatus(setStatus);

  onClick('account-signin-link', () => showAuthModal('signin'));
  onClick('panel-signup-btn', () => showAuthModal('signup'));
  onClick('account-sync-now', () => void Sync.syncNow());
  onClick('account-signout', signOut);

  onClick('auth-cancel', hideAuthModal);
  onClick('auth-submit', () => void submitAuth());
  onClick('auth-toggle-link', toggleMode);
  onClick('auth-forgot-link', () => void forgotPassword());

  const modal = must('auth-modal');
  modal.addEventListener('click', (event) => {
    if (event.target === modal) hideAuthModal();
  });

  input('auth-password').addEventListener('keydown', (event) => {
    if (event.key === 'Enter') void submitAuth();
  });

  // Delegated: the message is rebuilt on every state change, so the buttons
  // inside it are never the same elements twice.
  must('stats-empty').addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('button[data-action]');
    const action = button?.dataset['action'] as EmptyStateAction | undefined;
    if (action === 'signin') showAuthModal('signin');
    else if (action === 'restore') input('import-file').click();
    else if (action === 'sync') void Sync.syncNow();
  });

  render();
}
